/* eslint-disable react/prop-types */
import { Token } from '@kodiak-finance/sdk-core'
import { ButtonError, ButtonOutlined, ButtonPrimary } from 'components/Button'
import Column from 'components/Column'
import Row from 'components/Row'
import { RowBetween } from 'components/Row'
import { defaultChainId, SupportedChainId } from 'config/chains'
import { CHAIN_IDS_TO_NAMES } from 'config/chains'
import { secondsInDay, secondsInMonth, secondsInQuarter, secondsInWeek } from 'date-fns'
import { useConnection } from 'hooks/useConnection'
import { FarmErrors } from 'lib/hooks/kodiakFarms/useFarmAction'
import { MAX_REWARD_TOKENS } from 'pages/KodiakFarmsDeploy/constants'
import { calculateRewardRatePerSecond } from 'pages/KodiakFarmsDeploy/helper'
import { useCallback } from 'react'
import { AlertTriangle, ChevronRight, Minus, Plus } from 'react-feather'
import { Text } from 'rebass'
import styled from 'styled-components'
import { formatUnits } from 'viem'

import ConfigField from './ConfigField'
import { Container, FormGrid } from './styled'
import { FarmConfig, RewardToken } from './types'
import { getFieldError, hasValidationErrors } from './utils'

// Styled components for error messaging
const ErrorMessageBox = styled.div`
  margin: 1rem 0;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.error500};
  border-radius: 12px;
  background-color: rgba(220, 30, 30, 0.05);
`

const ErrorHeader = styled(Row)`
  align-items: center;
  margin-bottom: 12px;
`

const ErrorActions = styled(Row)`
  margin-top: 12px;
  gap: 12px;
`

const EditForm: React.FC<{
  config: FarmConfig
  originalConfig: FarmConfig
  allTokens: { [address: string]: Token }
  onRewardTokenChange: (index: number, field: keyof RewardToken, value: string) => void
  onFieldChange: (field: keyof Omit<FarmConfig, 'rewardTokens'>, value: string) => void
  errors: FarmErrors
  onReview: () => void
  onConfigChange: (config: FarmConfig) => void
  loading: boolean
  hasChanges: boolean
  setErrors: (errors: any) => void
  isBalanceCheckLoading: boolean
}> = ({
  config,
  allTokens,
  originalConfig,
  onRewardTokenChange,
  onFieldChange,
  errors,
  onReview,
  loading,
  hasChanges,
  onConfigChange,
  setErrors,
  isBalanceCheckLoading,
}) => {
  const { chainId } = useConnection()
  const chainName = chainId ? CHAIN_IDS_TO_NAMES[chainId as SupportedChainId] : CHAIN_IDS_TO_NAMES[defaultChainId()]

  const handleAddRewardToken = useCallback(() => {
    if (config.rewardTokens.length < MAX_REWARD_TOKENS) {
      onConfigChange({
        ...config,
        rewardTokens: [
          ...config.rewardTokens,
          {
            tokenAddress: '',
            rewardRate: '',
            rewardRateFormatted: '',
            rewardManager: '',
            id: '',
            address: '',
          },
        ],
      })
    }
  }, [config, onConfigChange])

  const handleRemoveRewardToken = useCallback(
    (indexToRemove: number) => {
      onConfigChange({
        ...config,
        rewardTokens: config.rewardTokens.filter((_, index) => index !== indexToRemove),
      })
    },
    [config, onConfigChange]
  )

  const handleResetChanges = useCallback(() => {
    onConfigChange(originalConfig)
    setErrors({})
  }, [originalConfig, onConfigChange, setErrors])

  // Check if there are any errors that should block proceeding to review
  const hasErrors = useCallback(() => {
    // Check for validation errors
    if (errors.validationErrors && errors.validationErrors.length > 0) {
      return true
    }

    // Check for balance errors
    if (errors.error && errors.error.type === 'BALANCE_ERROR') {
      return true
    }

    // Check if the errors object has any meaningful errors (not just an empty object)
    return hasValidationErrors(errors)
  }, [errors])

  // Render error message box
  const renderErrorMessage = useCallback(() => {
    if (!hasErrors()) return null

    // Handle wallet connection errors first
    if (errors.error && errors.error.type === 'VALIDATION_ERROR' && errors.error.field === 'wallet') {
      return (
        <ErrorMessageBox>
          <ErrorHeader>
            <AlertTriangle size={20} color="#DF1F38" style={{ marginRight: '8px' }} />
            <Text fontWeight={600} color="#DF1F38">
              Wallet Connection Required
            </Text>
          </ErrorHeader>

          <Text fontSize="14px" style={{ marginLeft: '28px' }}>
            {errors.error.message || 'Please connect your wallet to continue.'}
          </Text>

          <ErrorActions>
            <ButtonPrimary
              variant="primary"
              // onClick={() => {
              //   // Trigger your wallet connection function
              //   // This would usually come from a wallet connection hook
              //   // For example: connectWallet()
              // }}
              style={{ height: '36px' }}
            >
              Connect Wallet
            </ButtonPrimary>
          </ErrorActions>
        </ErrorMessageBox>
      )
    }

    // Handle balance errors
    if (errors.error && errors.error.type === 'BALANCE_ERROR') {
      const error = errors.error
      return (
        <ErrorMessageBox>
          <ErrorHeader>
            <AlertTriangle size={20} color="#DF1F38" style={{ marginRight: '8px' }} />
            <Text fontWeight={600} color="#DF1F38">
              Insufficient {error.symbol} Balance
            </Text>
          </ErrorHeader>

          <Text fontSize="14px" style={{ marginLeft: '28px' }}>
            You need{' '}
            <strong>
              {error.required} {error.symbol}
            </strong>{' '}
            but only have{' '}
            <strong>
              {error.available} {error.symbol}
            </strong>{' '}
            available.
          </Text>

          <Text fontSize="14px" style={{ marginLeft: '28px', marginTop: '8px' }}>
            Please reduce the reward rate in your configuration or add more tokens to your wallet. After making changes,
            click the <strong>Reset</strong> button to clear this error and try again.
          </Text>

          <ErrorActions>
            <ButtonOutlined onClick={handleResetChanges} style={{ height: '36px' }}>
              Reset Changes
            </ButtonOutlined>

            {error.token && (
              <ButtonPrimary
                variant="primary"
                onClick={() =>
                  window.open(
                    `https://app.kodiak.finance/#/swap?chain=${chainName}&inputCurrency=bera&outputCurrency=${error.token}`,
                    '_blank'
                  )
                }
                style={{ height: '36px' }}
              >
                Get {error.symbol}
              </ButtonPrimary>
            )}
          </ErrorActions>
        </ErrorMessageBox>
      )
    }

    // Handle network errors
    if (errors.error && errors.error.type === 'VALIDATION_ERROR' && errors.error.field === 'network') {
      return (
        <ErrorMessageBox>
          <ErrorHeader>
            <AlertTriangle size={20} color="#DF1F38" style={{ marginRight: '8px' }} />
            <Text fontWeight={600} color="#DF1F38">
              Network Connection Error
            </Text>
          </ErrorHeader>

          <Text fontSize="14px" style={{ marginLeft: '28px' }}>
            {errors.error.message ||
              'There was an issue connecting to the network. Please check your connection and try again.'}
          </Text>

          <ErrorActions>
            <ButtonOutlined onClick={handleResetChanges} style={{ height: '36px' }}>
              Reset Changes
            </ButtonOutlined>
          </ErrorActions>
        </ErrorMessageBox>
      )
    }

    // Handle generic errors
    if (errors.error && errors.error.type === 'GENERIC_ERROR') {
      return (
        <ErrorMessageBox>
          <ErrorHeader>
            <AlertTriangle size={20} color="#DF1F38" style={{ marginRight: '8px' }} />
            <Text fontWeight={600} color="#DF1F38">
              {errors.error.field ? `Error: ${errors.error.field}` : 'Error Detected'}
            </Text>
          </ErrorHeader>

          <Text fontSize="14px" style={{ marginLeft: '28px' }}>
            {errors.error.message || 'An unexpected error occurred. Please try again.'}
          </Text>

          <ErrorActions>
            <ButtonOutlined onClick={handleResetChanges} style={{ height: '36px' }}>
              Reset Changes
            </ButtonOutlined>
          </ErrorActions>
        </ErrorMessageBox>
      )
    }

    // Handle validation errors
    if (errors.validationErrors && errors.validationErrors.length > 0) {
      return (
        <ErrorMessageBox>
          <ErrorHeader>
            <AlertTriangle size={20} color="#DF1F38" style={{ marginRight: '8px' }} />
            <Text fontWeight={600} color="#DF1F38">
              Configuration Errors
            </Text>
          </ErrorHeader>

          <Text fontSize="14px" style={{ marginLeft: '28px' }}>
            Please fix the following errors before proceeding:
          </Text>

          <ul style={{ marginLeft: '48px', marginTop: '8px' }}>
            {errors.validationErrors.map((err, index) => (
              <li key={index}>
                <Text fontSize="14px">{err.message}</Text>
              </li>
            ))}
          </ul>

          <Text fontSize="14px" style={{ marginLeft: '28px', marginTop: '8px' }}>
            After making changes, you can click <strong>Reset</strong> to start over if needed.
          </Text>

          <ErrorActions>
            <ButtonOutlined onClick={handleResetChanges} style={{ height: '36px' }}>
              Reset Changes
            </ButtonOutlined>
          </ErrorActions>
        </ErrorMessageBox>
      )
    }

    // Default fallback error
    return (
      <ErrorMessageBox>
        <ErrorHeader>
          <AlertTriangle size={20} color="#DF1F38" style={{ marginRight: '8px' }} />
          <Text fontWeight={600} color="#DF1F38">
            Error Detected
          </Text>
        </ErrorHeader>

        <Text fontSize="14px" style={{ marginLeft: '28px' }}>
          There was an error with your configuration. Please check the highlighted fields and try again.
        </Text>

        <Text fontSize="14px" style={{ marginLeft: '28px', marginTop: '8px' }}>
          If you continue to have issues, you can click <strong>Reset</strong> to start over.
        </Text>

        <ErrorActions>
          <ButtonOutlined onClick={handleResetChanges} style={{ height: '36px' }}>
            Reset Changes
          </ButtonOutlined>
        </ErrorActions>
      </ErrorMessageBox>
    )
  }, [errors, hasErrors, handleResetChanges])

  return (
    <FormGrid>
      <Column gap="0.5rem">
        <RowBetween>
          <Text fontSize={18} fontWeight="600">
            Reward Token Configuration
          </Text>
          {config.rewardTokens.length < MAX_REWARD_TOKENS && (
            <ButtonOutlined
              onClick={handleAddRewardToken}
              style={{
                maxWidth: '160px',
                padding: '4px 8px',
                height: '32px',
              }}
            >
              <RowBetween>
                <Plus size={16} />
                <span style={{ fontSize: '14px' }}>Add Reward Token</span>
              </RowBetween>
            </ButtonOutlined>
          )}
        </RowBetween>
        {config.rewardTokens.map((rewardToken, index) => {
          const decimals = allTokens[rewardToken.tokenAddress]?.decimals ?? 18
          const isNewlyAddedToken = index >= originalConfig.rewardTokens.length

          return (
            <Container key={index}>
              <FormGrid>
                <ConfigField
                  label={`Reward Token ${index + 1}`}
                  value={rewardToken.tokenAddress}
                  onChange={(value) => onRewardTokenChange(index, 'tokenAddress', value)}
                  error={getFieldError(`rewardTokenAddress${index}`, errors)}
                  info="The ERC20 token that will be distributed as rewards to stakers in this farm"
                  placeholder="Enter token address (0x...)"
                  allTokens={allTokens}
                  isRewardToken
                  currentValue={rewardToken.tokenAddress}
                  disabled={isNewlyAddedToken ? null : true}
                />
                <ConfigField
                  label="Reward Rate"
                  value={rewardToken.rewardRateFormatted}
                  onChange={(value) => onRewardTokenChange(index, 'rewardRateFormatted', value)}
                  error={getFieldError(`rewardRateFormatted${index}`, errors)}
                  info="Enter monthly reward rate"
                  placeholder="Enter monthly reward rate"
                  subText={formatUnits(
                    calculateRewardRatePerSecond(
                      rewardToken.rewardRateFormatted,
                      parseInt(config.rewardsDuration.toString()),
                      decimals
                    ),
                    decimals
                  )}
                />
                <ConfigField
                  label="Reward Manager"
                  value={rewardToken.rewardManager}
                  onChange={(value) => onRewardTokenChange(index, 'rewardManager', value)}
                  error={getFieldError(`rewardManager${index}`, errors)}
                  info="Address authorized to manage reward distribution for this token. Can add more rewards and configure distribution"
                  placeholder="Enter reward manager address (0x...)"
                />
                {isNewlyAddedToken && (
                  <ButtonError
                    onClick={() => handleRemoveRewardToken(index)}
                    style={{ marginTop: '1rem', gap: '4px' }}
                    error
                  >
                    <Minus size={16} />
                    Remove Token
                  </ButtonError>
                )}
              </FormGrid>
            </Container>
          )
        })}
      </Column>

      <ConfigField
        label="Staking Token Cap"
        value={config.stakingTokenCap.toString()}
        onChange={(value) => onFieldChange('stakingTokenCap', value)}
        error={getFieldError('stakingTokenCap', errors)}
        info="Maximum amount of staking tokens allowed in the farm. Enter -1 for unlimited cap (max uint256)"
        placeholder="Enter staking token cap"
      />

      <ConfigField
        label="Maximum Multiplier"
        value={config.lockMultiplierMax.toString()}
        onChange={(value) => onFieldChange('lockMultiplierMax', value)}
        error={getFieldError('maxMultiplier', errors)}
        info="Maximum reward multiplier that users can earn by locking tokens. E.g., 3 means users can earn up to 3x rewards"
        placeholder="Enter maximum multiplier"
        isSlider
        sliderSelector={{
          min: 1,
          max: 5,
          step: 1,
          size: 24,
          value: `${config.lockMultiplierMax.toString()}x`,
        }}
      />

      <ConfigField
        label="Minimum Lock Time"
        value={config.lockTimeMin.toString()}
        onChange={(value) => onFieldChange('lockTimeMin', value)}
        error={getFieldError('lockTimeMin', errors)}
        info="Minimum duration in seconds users must lock their tokens. Users cannot withdraw before this period expires"
        isSlider
        sliderSelector={{
          min: 0,
          max: secondsInQuarter,
          step: secondsInDay,
          size: 24,
          value: `${parseInt(config.lockTimeMin.toString()) / secondsInDay} day(s)`,
        }}
        placeholder=""
      />

      <ConfigField
        label="Lock Time for Max Multiplier"
        value={config.lockTimeMax.toString()}
        onChange={(value) => onFieldChange('lockTimeMax', value)}
        error={getFieldError('lockTimeForMaxMultiplier', errors)}
        info="Duration in seconds users must lock their tokens to receive the maximum reward multiplier. Must be greater than minimum lock time"
        isSlider
        sliderSelector={{
          min: 0,
          max: secondsInMonth * 6,
          step: secondsInDay,
          size: 24,
          value:
            parseFloat(config.lockTimeMax.toString()) / secondsInDay < 0.01
              ? `0 day(s)`
              : `${parseInt(config.lockTimeMax.toString()) / secondsInDay} day(s)`,
        }}
        placeholder=""
      />

      <ConfigField
        label="Rewards Duration"
        value={config.rewardsDuration.toString()}
        onChange={(value) => onFieldChange('rewardsDuration', value)}
        error={getFieldError('rewardsDuration', errors)}
        info="Duration for each reward period cycle. Determines how long rewards will be distributed before needing renewal"
        isSlider
        sliderSelector={{
          min: secondsInWeek,
          max: secondsInMonth * 6,
          step: secondsInDay,
          value: `${parseInt(config.rewardsDuration.toString()) / secondsInDay} day(s)`,
        }}
        placeholder=""
      />

      {renderErrorMessage()}

      <RowBetween>
        <ButtonOutlined
          onClick={handleResetChanges}
          style={{
            marginTop: '2rem',
            width: 'fit-content',
            justifySelf: 'start',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          Reset
        </ButtonOutlined>

        <ButtonPrimary
          variant="primary"
          onClick={onReview}
          disabled={loading || !hasChanges || hasErrors() || isBalanceCheckLoading}
          style={{
            marginTop: '2rem',
            width: 'fit-content',
            justifySelf: 'end',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          Review Changes
          <ChevronRight size={16} />
        </ButtonPrimary>
      </RowBetween>
    </FormGrid>
  )
}

export default EditForm
